/**
 * Fehlen Credits für einen Vorgang? — aus der 402-Antwort des Backends
 * (`plan_limit_reached:credits:current=<frei>:limit=<benötigt>`,
 * FLEET_CREDITS_SPEC §4.4).
 *
 * Gelesen wird nur über `errorCode` (Regel #23: niemand sonst liest `detail`).
 * Was fehlt, steht in den zwei Zahlen des Codes; welches Paket es deckt, kommt
 * aus dem Katalog (`useKatalog`), nicht aus der Antwort.
 */
import { anzahlText } from '../shared/anzahl'
import { errorCode } from '../shared/errorDetail'
import type { Uebersetzer } from './fehlerText'
import { getLocale } from './locale'

export interface CreditsFehlen {
  /** Was das Guthaben noch hergibt. */
  frei: number
  /** Was der Vorgang kostet. */
  benoetigt: number
  /** Der Unterschied — nie negativ. */
  fehlt: number
}

const MUSTER = /^plan_limit_reached:credits:current=(\d+):limit=(\d+)$/

export function creditsFehlen(err: unknown): CreditsFehlen | null {
  const code = errorCode(err)
  const treffer = code ? MUSTER.exec(code) : null
  if (!treffer) return null
  const frei = Number(treffer[1])
  const benoetigt = Number(treffer[2])
  return { frei, benoetigt, fehlt: Math.max(0, benoetigt - frei) }
}

/**
 * Der Satz zu fehlenden Credits — für jede Stelle, die einen Fehler als Satz
 * zeigt (`fehlerText`), und für die Karte (`CreditsFehlen.tsx`).
 *
 * Ohne `locale` gilt die Sprache der Seite (`<html lang>`, gesetzt von
 * `HreflangTags`) — `fehlerText` kennt nur `t`.
 */
export function creditsFehlenSatz(f: CreditsFehlen, t: Uebersetzer, locale?: string): string {
  const sprache = locale ?? getLocale(typeof document !== 'undefined' ? document.documentElement.lang : '')
  // `anzahlText` reicht seine Werte als `unknown` weiter — hier sind es Zahlen als Text.
  const alsText = (werte: Record<string, unknown> = {}) =>
    Object.fromEntries(Object.entries(werte).map(([name, wert]) => [name, String(wert)]))
  return anzahlText(
    (key, vorgabe, werte) => t(key, vorgabe, alsText(werte)), sprache, f.fehlt,
    'credits.fehlen.satz_one', 'Dafür fehlt dir {count} Credit (frei: {frei}, gebraucht: {benoetigt}).',
    'credits.fehlen.satz_other', 'Dafür fehlen dir {count} Credits (frei: {frei}, gebraucht: {benoetigt}).',
    { frei: f.frei.toLocaleString(sprache), benoetigt: f.benoetigt.toLocaleString(sprache) },
  )
}
